import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalHeader from '../../components/Portal/PortalHeader';
import ProductCard from '../../components/Portal/ProductCard';
import ProductCarousel from '../../components/Portal/ProductCarousel';
import LoginModal from '../../components/Portal/LoginModal';
import ProductDetailModal from '../../components/Portal/ProductDetailModal';
import { useNewProducts, usePublicProducts } from '../../hooks/portal/usePortal';
import { useAuth } from '../../contexts/AuthContext';
import heroBanner from '../../assets/images/hero_banner.png';
import './PortalHome.scss';

const PortalHome = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setShowProductModal(true);
  };

  // Cargar productos según autenticación
  const { products: authenticatedProducts = [], loading: loadingAuth } = isAuthenticated
    ? useNewProducts(5)
    : { products: [], loading: false };

  const { products: publicProducts = [], loading: loadingPublic } = !isAuthenticated
    ? usePublicProducts(10)
    : { products: [], loading: false };

  const products = isAuthenticated ? authenticatedProducts : publicProducts;
  const loading = isAuthenticated ? loadingAuth : loadingPublic;

  const allMenuOptions = [
    {
      id: 'crear-orden',
      title: 'CREAR ORDEN',
      path: '/portal/crear-orden',
      icon: '🛒',
      authRequired: true
    },
    {
      id: 'pagar-factura',
      title: 'PAGAR FACTURA',
      path: '/portal/pagar-factura',
      icon: '💳',
      authRequired: true
    },
    {
      id: 'estado-cuenta',
      title: 'ESTADO DE CUENTA',
      path: '/portal/estado-cuenta',
      icon: '📊',
      authRequired: true
    },
    {
      id: 'historial',
      title: 'HISTORIAL DE COMPRAS',
      path: '/portal/historial',
      icon: '📋',
      authRequired: true
    },
    {
      id: 'nuevos',
      title: 'NUEVOS PRODUCTOS',
      path: '/portal/nuevos-productos',
      icon: '✨',
      authRequired: false
    },
    {
      id: 'ofertas',
      title: 'OFERTAS',
      path: '/portal/ofertas',
      icon: '🏷️',
      authRequired: false
    }
  ];

  // Filtrar opciones según autenticación
  const menuOptions = isAuthenticated
    ? allMenuOptions
    : allMenuOptions.filter(option => !option.authRequired);

  const handleNavigation = (path) => {
    // Si no está autenticado, abrir modal de login
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    // Si está autenticado, navegar normalmente
    navigate(path);
  };

  return (
    <div className="portal-home">
      <PortalHeader />

      {/* Hero Section */}
      <section
        className="portal-hero"
        style={{ backgroundImage: `url(${heroBanner})` }}
      >
      </section>

      {/* Menu de Opciones */}
      <section className="portal-menu">
        <div className="portal-menu__container">
          {menuOptions.map((option) => (
            <button
              key={option.id}
              className="portal-menu__button"
              onClick={() => handleNavigation(option.path)}
            >
              {option.title}
            </button>
          ))}
        </div>
      </section>

      {/* Mira lo Nuevo */}
      <section className="portal-new-products">
        <div className="portal-new-products__container">
          <div className="portal-new-products__header">
            <h2 className="portal-new-products__title">
              MIRA LO
              <br />
              NUEVO
            </h2>
          </div>

          <div className="portal-new-products__content">
            <div className="portal-new-products__arrow">
              <svg viewBox="0 0 100 120" xmlns="http://www.w3.org/2000/svg">
                {/* Línea vertical bajando */}
                <line x1="30" y1="0" x2="30" y2="50" stroke="#7b2c7f" strokeWidth="10" strokeLinecap="round"/>

                {/* Curva del codo */}
                <path d="M 30,50 Q 30,70 50,70" stroke="#7b2c7f" strokeWidth="10" fill="none" strokeLinecap="round"/>

                {/* Línea horizontal hacia la derecha */}
                <line x1="50" y1="70" x2="80" y2="70" stroke="#7b2c7f" strokeWidth="10" strokeLinecap="round"/>

                {/* Punta de flecha */}
                <polygon points="90,70 70,60 70,80" fill="#7b2c7f"/>
              </svg>
            </div>

            {/* Modo Público: Carousel */}
            {!isAuthenticated ? (
              <div className="portal-new-products__carousel-container">
                {loading ? (
                  <div className="portal-new-products__loading">
                    <div className="spinner"></div>
                    <p>Cargando productos...</p>
                  </div>
                ) : (
                  <ProductCarousel products={products} onProductClick={handleProductClick} />
                )}
              </div>
            ) : (
              /* Modo Autenticado: Grid de Cards */
              <div className="portal-new-products__grid-container">
                <div className="portal-new-products__grid">
                  {loading ? (
                    <div className="portal-new-products__loading">
                      <div className="spinner"></div>
                      <p>Cargando productos nuevos...</p>
                    </div>
                  ) : products && products.length > 0 ? (
                    products.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        showBadge={true}
                        onLoginRequired={() => setShowLoginModal(true)}
                      />
                    ))
                  ) : (
                    <div className="portal-new-products__empty">
                      <p>No hay productos nuevos en este momento</p>
                    </div>
                  )}
                </div>

                {products && products.length > 0 && (
                  <div className="portal-new-products__view-all">
                    <button
                      className="portal-new-products__view-all-btn"
                      onClick={() => navigate('/portal/nuevos-productos')}
                    >
                      Ver todos los productos nuevos
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />

      <ProductDetailModal
        product={selectedProduct}
        isOpen={showProductModal}
        onClose={() => setShowProductModal(false)}
        onLoginRequired={() => setShowLoginModal(true)}
      />
    </div>
  );
};

export default PortalHome;
